/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Typography,
  Divider,
  Paper,
  Stack,
} from '@mui/material';
import styles from './AllDayReport.module.css';
import { getSemenProcessingPreProcesss, SumationData } from './AllDayHelper';
import GradingUnavailable from '../Occyte-Grading/oocyteGradingUnavailable';
import EmbryoImageLightbox from './EmbryoImageLightbox';
import { getButtonStates } from '../../../utils/permissionService';
import { ReportFooterTimestamp } from '../../../utils/reportUtils';

interface cycleProp {
  selectedCycleId: number;
}

/** Group Day rows into embryo → { embryo_number, embryo_no, days: {0:{},1:{}} } **/
const groupByEmbryo = (rows: any[]) => {
  return rows
    .filter((r) => r && r.embryo_number != null)
    .reduce((acc: any[], curr: any) => {
      const idx = acc.findIndex((e) => e.embryo_number === curr.embryo_number);
      if (idx === -1) {
        acc.push({
          embryo_number: curr.embryo_number,
          embryo_no: curr.embryo_no ?? curr.embryo_number,
          days: { [curr.day]: curr },
        });
      } else {
        acc[idx].days[curr.day] = curr;
        if (curr.embryo_no) acc[idx].embryo_no = curr.embryo_no;
      }
      return acc;
    }, []);
};

const AllDayReport: React.FC<cycleProp> = ({ selectedCycleId }) => {
  const [dataAllDay, setDataAllDay] = useState<any[]>([]);
  const [pgtData, setPgtData] = useState<any[]>([]);
  const [summationData, setSummationData] = useState<SumationData | undefined>();

  const statusKeys = [
    'gv',
    'mi',
    'mii',
    'fertilized',
    'cleaved',
    'blast',
    'discarded',
    'freezed',
    'transferred',
  ];

  const { t: tGeneral } = useTranslation('general');
  const { t: tCulture } = useTranslation('culture_config');
  const { t: tOocyteGrading } = useTranslation('oocyte_grading');

  const [lightboxData, setLightboxData] = useState<{
    imageUrl: string | null;
    embryo_no: number | null;
    label: string | null;
  }>({ imageUrl: null, embryo_no: null, label: null });

  const { canView } = getButtonStates('Lab', 'Embryo Culture', 'ALL', 'female');

  useEffect(() => {
    if (!canView) return;

    getSemenProcessingPreProcesss(selectedCycleId)
      .then((response) => {
        const sortedResponse = response.all_days.sort(
          (a: any, b: any) => a.embryo_number - b.embryo_number
        );
        setDataAllDay(sortedResponse);

        const pgtInfo = sortedResponse.filter(
          (item: any) => item.has_pgt === true || item.has_pgt === false
        );
        setPgtData(pgtInfo);

        setSummationData(response.summation);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching data:', error);
      });
  }, [selectedCycleId, canView]);

  if (!canView) {
    return (
      <GradingUnavailable
        message={tGeneral('youDoNotHaveAccessToViewPage')}
      />
    );
  }

  const daysWithData = [0, 1, 2, 3, 4, 5, 6, 7].filter((d) =>
    dataAllDay.some((row) => row.day === d && row.embryo_number !== null)
  );

  const groupedEmbryos = groupByEmbryo(dataAllDay);

  const findPgtForEmbryo = (embryo_number: number) =>
    pgtData.find((p: any) => p.embryo_number === embryo_number) || {};

  const renderDayDetails = (dayIndex: number, dayData: any) => {
    if (!dayData) return '-';

    // Day 0 — Oocyte Maturity
    if (dayIndex === 0) {
      const maturity =
        dayData.oocyte_grade
          ? tOocyteGrading(dayData.oocyte_grade.replace(/\s+/g, '').toLowerCase())
          : '-';

      return (
        <>
          <div><strong>{tCulture('maturity')}:</strong> {maturity}</div>
        </>
      );
    }

    // Day 1 — PN + PB
    if (dayIndex === 1) {
      return (
        <>
          <div><strong>{tCulture('pn')}:</strong> {dayData.pn_label || dayData.pn || '-'}</div>
          <div><strong>{tCulture('pb')}:</strong> {dayData.pb_label || dayData.pb || '-'}</div>
        </>
      );
    }

    // Day 2 & Day 3 — Cells / Grade / Frag
    if (dayIndex === 2 || dayIndex === 3) {
      return (
        <>
          <div>
            <strong>{tGeneral('numberOfCells')}:</strong> {dayData.number_of_cells_label || dayData.number_of_cells || '-'}
          </div>
          <div>
            <strong>{tGeneral('grade')}:</strong> {dayData.grade || '-'}
          </div>
          {dayIndex === 2 && (
            <div>
              <strong>{tGeneral('fragmentation')}:</strong>{' '}
              {dayData.fragmentation_percentage_label ||
              dayData.fragmentation_percentage ||
              '-'}
            </div>
          )}
        </>
      );
    }

    // Day 4 & Day 5 — Stage / Grade (and AI Score for Day 5)
    return (
      <>
        <div><strong>{tGeneral('stage')}:</strong> {dayData.stage_label || dayData.stage || '-'}</div>
        <div><strong>{tGeneral('grade')}:</strong> {dayData.grade || '-'}</div>
        {dayIndex >= 5 && (
          <div><strong>{tGeneral('aiScore')}:</strong> {dayData.ai_score ?? '-'}</div>
        )}
      </>
    );
  };


  return (
    <>
      {groupedEmbryos.length > 0 ? (
        <>
          {/* SUMMARY ROW */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            padding={2}
            marginX={2.5}
          >
            {statusKeys.map((key, index) => (
              <Box key={key} className={styles.summaryItem}>
                <Typography
                  variant="body2"
                  sx={{ textTransform: 'capitalize', fontWeight: 700 }}
                >
                  {tGeneral(key)}
                </Typography>

                {summationData && (
                  <Chip label={summationData[key]} className={styles.summaryChip} />
                )}

                {index !== statusKeys.length - 1 && (
                  <Typography variant="body2" component="span" className={styles.vbar}>
                    |
                  </Typography>
                )}
              </Box>
            ))}
          </Box>

          {/* EMBRYO CARDS */}
          <div className={styles.cardsWrapper}>
            {groupedEmbryos.map((emb: any) => {
              const pgt = findPgtForEmbryo(emb.embryo_number);

              return (
                <React.Fragment key={emb.embryo_number}>
                  <Paper className={styles.embryoCard}>
                    {/* HEADER */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" className={styles.cardTitle}>
                        {tGeneral('embryoNo')} {emb.embryo_no}
                      </Typography>
                    </Stack>

                    <Divider sx={{ my: 1 ,borderColor: '#6e6e6e' }} />

                    {/* DAY-WISE TABLE */}
                    <div className={styles.dayTable}>
                      {daysWithData.map((dayIndex) => {
                        const dayData = emb.days?.[dayIndex] || null;
                        const files = dayData?.files || [];

                        return (
                          <div className={styles.dayRow} key={`${emb.embryo_number}_d${dayIndex}`}>
                            {/* DAY LABEL */}
                            <div className={styles.dayLabel}>
                              <Typography variant="subtitle2">
                                {tGeneral('day')} {dayIndex}
                              </Typography>
                            </div>

                            {/* DETAILS */}
                            <div className={styles.dayDetails}>
                              {renderDayDetails(dayIndex, dayData)}
                            </div>

                            {/* IMAGES */}
                            <div className={styles.dayImages}>
                              {files.length > 0 ? (
                                <div className={styles.thumbWrap}>
                                  {files.map((f: any, i: number) => (
                                    <img
                                      key={i}
                                      src={f.file_proxy_url || f.file || '/fallback-image.jpg'}
                                      alt='myImage'
                                      className={styles.thumb}
                                      onClick={() =>
                                        setLightboxData({
                                          imageUrl: f.file_proxy_url || '',
                                          embryo_no: emb.embryo_number,
                                          label: `${tGeneral('embryoNo')}: ${emb.embryo_number} ${tGeneral('day')}: ${dayIndex}`,
                                        })
                                      }
                                    />
                                  ))}
                                </div>
                              ) : (
                                <Typography variant="body2" className={styles.noImage}>-</Typography>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <Divider sx={{ my: 1 }} />

                    {/* PGT + AH MOVED HERE (BOTTOM BEFORE RESULT) */}
                    <div className={styles.cardFooter}>
                      <div>
                        <strong>{tCulture('pgt')}:</strong>&nbsp;
                        {pgt.has_pgt === true
                          ? tGeneral('yes')
                          : pgt.has_pgt === false
                            ? tGeneral('no')
                            : '-'}
                      </div>

                      <div>
                        <strong>{tCulture('ah')}:</strong>&nbsp;
                        {pgt.assisted_hatching === true
                          ? tGeneral('yes')
                          : pgt.assisted_hatching === false
                            ? tGeneral('no')
                            : '-'}
                      </div>
                    </div>

                    <Divider sx={{ my: 1 }} />

                    {/* RESULT & DISPOSITION */}
                    <div className={styles.cardFooter}>
                      <div>
                        <strong>{tGeneral('result')}:</strong>&nbsp;-
                      </div>
                      <div>
                        <strong>{tGeneral('disposition')}:</strong>&nbsp;
                        {(() => {
                          const disposition = (Object.values(emb.days || {}) as any[])
                            .find((d) => d?.disposition)?.disposition;

                          if (disposition === 'FREEZE') return tOocyteGrading('freeze');
                          if (disposition === 'CULTURE') return tCulture('culture');
                          if (disposition === 'DISCARD') return tCulture('discard');
                          if (disposition === 'TRANSFER') return tCulture('transfer');

                          return disposition || '-';
                        })()}
                      </div>
                    </div>

                  </Paper>
                  {groupedEmbryos.indexOf(emb) !== groupedEmbryos.length - 1 && <Divider sx={{ my: 1, borderColor: '#6e6e6e' }} />}
                </React.Fragment>
              );
            })}
            
          </div>
          <ReportFooterTimestamp />

          {/* LIGHTBOX */}
          {lightboxData.imageUrl && (
            <EmbryoImageLightbox
              imageUrl={lightboxData.imageUrl}
              embryoNumber={lightboxData.embryo_no}
              label={lightboxData.label}
              onClose={() => setLightboxData({ imageUrl: null, embryo_no: null, label: null })}
            />
          )}
        </>
      ) : (
        <GradingUnavailable message={tCulture('summaryUnavailable')} />
      )}
    </>
  );
};

export default AllDayReport;
